import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { hasRequiredRole, initAuth, logout, REQUIRED_ROLE } from './auth/keycloak';
import { I18nProvider, t } from './i18n';
import { ThemeProvider } from './components/theme-provider';
import { Spinner } from './components/ui/spinner';
import './styles/index.css';

const container = document.getElementById('root')!;

declare global {
  interface Window {
    __react_root__?: ReturnType<typeof createRoot>;
    __auth_initialized__?: boolean;
  }
}

const root = window.__react_root__ || createRoot(container);
window.__react_root__ = root;

function Gate({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="gate">
      <div className="gate-card">
        <div className="flex items-center gap-3 mb-4">
          <img
            src="/logo.png"
            alt="APUA – ASEA"
            className="h-10 w-10 object-contain shrink-0"
          />
          <span className="font-serif text-xs font-semibold tracking-widest text-gold2 uppercase">
            UPBEAT · APUA – ASEA
          </span>
        </div>
        <h1>{title}</h1>
        {children}
      </div>
    </main>
  );
}

function LoadingGate() {
  return (
    <main className="min-h-screen w-full flex flex-col items-center justify-center p-6 bg-space text-txt">
      <div className="flex flex-col items-center gap-6 max-w-sm text-center select-none animate-in fade-in duration-500">
        <div className="relative flex items-center justify-center">
          <Spinner size="2xl" className="text-gold" />
          <img
            src="/logo.png"
            alt="APUA – ASEA"
            className="absolute h-16 w-16 object-contain rounded-full select-none drop-shadow-md"
          />
        </div>
        <div className="space-y-1.5 select-none">
          <h1 className="font-serif text-xl sm:text-2xl font-bold tracking-tight text-txt">
            UPBEAT · APUA – ASEA
          </h1>
          <p className="font-sans text-xs font-semibold tracking-widest text-gold2 uppercase">
            Utility Performance Benchmarking
          </p>
        </div>
      </div>
    </main>
  );
}

// components, not inline JSX, so `t` is read inside the provider
function NoAccess() {
  return (
    <Gate title={t.noAccessTitle}>
      <p>{t.noAccessBody(<code>{REQUIRED_ROLE}</code>)}</p>
      <button type="button" className="menu" onClick={logout}>{t.signOut}</button>
    </Gate>
  );
}

function Unavailable() {
  return (
    <Gate title={t.unavailableTitle}>
      <p>{t.unavailableBody}</p>
      <button type="button" className="menu" onClick={() => window.location.reload()}>{t.retry}</button>
    </Gate>
  );
}

function renderApp() {
  root.render(
    <StrictMode>
      <ThemeProvider defaultTheme="system" storageKey="upbeat-theme">
        <I18nProvider>
          {hasRequiredRole() ? <App /> : <NoAccess />}
        </I18nProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}

if (!window.__auth_initialized__) {
  // Render immediate loading state so the user does not experience a blank white screen
  root.render(
    <ThemeProvider defaultTheme="system" storageKey="upbeat-theme">
      <LoadingGate />
    </ThemeProvider>,
  );

  // Keycloak must finish (or redirect to its login page) before anything renders.
  initAuth()
    .then(() => {
      window.__auth_initialized__ = true;
      renderApp();
    })
    .catch((err: unknown) => {
      console.error('keycloak init failed', err);
      root.render(
        <ThemeProvider defaultTheme="system" storageKey="upbeat-theme">
          <I18nProvider>
            <Unavailable />
          </I18nProvider>
        </ThemeProvider>,
      );
    });
} else {
  // During Vite HMR, re-render directly without wiping DOM or resetting auth state
  renderApp();
}
